import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Flex, Form, Typography } from 'antd';
import { useProject } from '@contexts/Project.context';
import { useConfirm } from '@contexts/Confirm.context';
import SidebarTemplate from '../../../sidebars/sidebarElements/SidebarTemplate';
import FilterList from '@components/propertyFilters/FilterList';
import FilterActions from '@components/propertyFilters/FilterActions';
import useCloseSidebarWhenEmpty from '@hooks/useCloseSidebarWhenEmpty';
import { COLLECTION_SIDEBAR_IDS } from '../constants';

const { Text } = Typography;

/**
 * Collection filter panel — renders into the modify-layers portal.
 *
 * Presentational: receives the `filters` hook instance from CollectionResults
 * rather than calling useCollectionFilters internally. This keeps filter state
 * owned at the CollectionResults level where useFilteredFeatures can consume it
 * directly without routing through the Zustand store.
 *
 * TODO: Replace createPortal + SidebarTemplate with antd Drawer for built-in
 *       focus management, keyboard support, animations, and consistent styling.
 *       Keeping current pattern for UX consistency with other secondary sidebars.
 */
const CollectionFilter = ({ collectionData, filters, histogramValues }) => {
	const { modifyLayersSidebarRef, setActiveSidebar } = useProject();
	const { confirm } = useConfirm();

	const {
		form,
		initialValues,
		filterConfigs,
		isDirty,
		isSaving,
		activeFilters,
		dirtyFilters,
		shownCount,
		totalCount,
		clearFilter,
		apply,
		reset,
		handleValuesChange,
	} = filters;

	const handleHide = useCallback(async () => {
		if (!isDirty) return;

		const confirmed = await confirm({
			title: 'Discard changes?',
			message: 'You have unsaved changes. Are you sure you want to close without applying?',
			confirmLabel: 'Discard',
			cancelLabel: 'Keep editing',
		});

		if (confirmed) {
			reset();
		} else {
			setActiveSidebar({
				sidebarId: COLLECTION_SIDEBAR_IDS.collectionFilter,
				parentId: 'layerView',
			});
		}
	}, [isDirty, confirm, reset, setActiveSidebar]);

	// The configs can empty out under an open panel while this stays mounted.
	useCloseSidebarWhenEmpty(COLLECTION_SIDEBAR_IDS.collectionFilter, filterConfigs.length > 0);

	if (!modifyLayersSidebarRef?.current) return null;
	if (!filterConfigs.length) return null;

	return createPortal(
		<SidebarTemplate
			zindex="2"
			sidebarId={COLLECTION_SIDEBAR_IDS.collectionFilter}
			header={collectionData?.name ?? 'Filter Collection'}
			closeable
			onHide={handleHide}>
			<Flex vertical style={{ height: '100%', overflow: 'hidden' }}>
				{/* Description */}
				<Flex vertical gap={8} style={{ padding: '12px 16px' }}>
					<Text type="secondary" style={{ fontSize: '0.8rem' }}>
						{collectionData?.objectClassCount ?? 0} object classes &middot; applies to
						every object &middot; previews live on the map
					</Text>
					<Alert
						type="info"
						showIcon
						message="Applies to all objects. Object-level ranges will be clamped to these values."
						style={{ fontSize: '0.75rem', wordBreak: 'normal' }}
					/>
				</Flex>

				{/* Filter fields */}
				<Form
					form={form}
					initialValues={initialValues}
					component={false}
					onValuesChange={handleValuesChange}>
					<Flex
						vertical
						style={{
							flex: 1,
							overflowY: 'auto',
							scrollbarGutter: 'stable',
							padding: '4px 0 16px',
						}}>
						<FilterList
							filters={filterConfigs}
							activeFilters={activeFilters}
							dirtyFilters={dirtyFilters}
							isDirty={isDirty}
							shownCount={shownCount}
							totalCount={totalCount}
							histogramValues={histogramValues}
							onClear={clearFilter}
						/>
					</Flex>
				</Form>

				{/* Footer */}
				<FilterActions
					isDirty={isDirty}
					isSaving={isSaving}
					onApply={apply}
					onReset={reset}
				/>
			</Flex>
		</SidebarTemplate>,
		modifyLayersSidebarRef.current
	);
};

export default CollectionFilter;
